import { createSlice } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid/non-secure';

import { ISelectValue } from '@/shared/types/select';
import { IUserContact } from '@/shared/types/userContact';

import {
  IActor,
  IActorAward,
  IActorEducation,
  TActorErrors
} from '../types/Actor';
import { TSkillCategory } from '../types/UserBase';

/** Год и название награды; `id` — у правленой записи, без него — новая */
interface IAwardUpdate {
  data: Pick<IActorAward, 'year' | 'title'>;
  id?: IActorAward['id'];
}

interface IState {
  data: IActor;
  errors: TActorErrors;
  isSubmitting: boolean;
}

const initialState: IState = {
  data: {} as IActor,
  errors: {} as TActorErrors,
  isSubmitting: false
};

const profileToEditSlice = createSlice({
  name: 'profileToEdit',
  initialState,
  reducers: {
    /**
     * Обновление данных пользователя
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Состояние, которое будет объединено с текущим состоянием.
     */
    update: (state, { payload }: { payload: Partial<IState> }) => ({
      ...state,
      ...payload
    }),

    /**
     * Частичное обновление данных пользователя
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Частичное состояние, которое будет объединено с текущим состоянием.
     */
    updateData: (state, { payload }: { payload: Partial<IActor> }) => ({
      ...state,
      data: { ...state.data, ...payload }
    }),

    /**
     * Обновление ошибок
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Ошибки, которые будут объеденены с текущим состоянием.
     */
    updateErrors: (state, { payload }: { payload: TActorErrors }) => ({
      ...state,
      errors: payload
    }),

    /**
     * Сброс ошибок
     * @param state Состояние
     */
    resetErrors: state => ({
      ...state,
      errors: initialState.errors
    }),

    /**
     * Обновляем видимость портфолио
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Новое значение видимости портфолио
     */
    updateProfileVisibility: (
      state,
      { payload }: { payload: ISelectValue }
    ) => {
      state.data.profile_visibility = payload;
    },

    /**
     * Переключатель редактирования образований
     * @param state Состояния
     * @param root0 Экшен
     * @param root0.payload Id образования
     */
    toggleEditEducation: (
      state,
      { payload }: { payload: string | undefined }
    ) => {
      if (!payload) return state;

      const newEducations =
        state.data.educational_establishments_attributes.map(education => {
          if (education.id === payload) {
            return {
              ...education,
              _edit: !education._edit
            };
          }

          return education;
        });
      state.data.educational_establishments_attributes = newEducations;
    },

    /**
     * Обновляем массив данных актера c образованием
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Значение education, которое будет объединено с текущим состоянием.
     * @param action.payload.data - Данные из заполненной формы
     * @param action.payload.id - id образования
     */
    updateEducation: (
      state,
      { payload }: { payload: { data: IActorEducation; id?: string } }
    ) => {
      const { data, id } = payload;
      let newEducation;

      if (id) {
        const existingJobIndex =
          state.data.educational_establishments_attributes.findIndex(
            attribute => attribute.id === id
          );

        if (existingJobIndex >= 0) {
          newEducation = {
            id,
            _new: state.data.educational_establishments_attributes[
              existingJobIndex
            ]._new,
            ...data
          };
          state.data.educational_establishments_attributes[existingJobIndex] =
            newEducation;
        }
      } else {
        newEducation = {
          id: nanoid(),
          _new: true,
          ...data
        };
        state.data.educational_establishments_attributes.push(newEducation);
      }
    },

    /**
     * Удаляем образование
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Id образования, которое будет удалено.
     */
    deleteEducation: (state, { payload }: { payload: string | undefined }) => {
      if (!payload) return state;
      /** Проходим по коллекции и новый массив помещаем в jobs_attributes */
      state.data.educational_establishments_attributes =
        state.data.educational_establishments_attributes
          .map(education => {
            /** Если объект новый, его не добавляем в новый массив */
            if (education.id === payload && education._new === true) {
              return null;
            }
            /** Если объект не новый, добавляем поле _destroy: true, если id совпадает */
            if (education.id === payload) {
              return { ...education, _destroy: true };
            }
            return education;
          })
          .filter(education => education !== null);
    },

    /**
     * Переключатель редактирования опыта работы
     * @param state Состояния
     * @param root0 Экшен
     * @param root0.payload Id опыта работы
     */
    toggleEditJob: (state, { payload }: { payload: string | undefined }) => {
      if (!payload) return state;

      const newJob = state.data.jobs_attributes.map(job => {
        if (job.id === payload) {
          return {
            ...job,
            _edit: !job._edit
          };
        }

        return job;
      });
      state.data.jobs_attributes = newJob;
    },

    /**
     * Обновляем массив данных актера c опытом работы
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Значение job, которое будет объединено с текущим состоянием.
     * @param action.data - Данные из заполненной формы
     * @param action.projectType - тип работы
     * @param action.id - id работы
     */
    updateJobs: (state, action) => {
      const { data, projectType, id } = action.payload;
      let newJobItem;

      if (id) {
        const existingJobIndex = state.data.jobs_attributes.findIndex(
          attribute => attribute.id === id
        );

        if (existingJobIndex >= 0) {
          newJobItem = {
            id,
            project_type: projectType,
            _new: state.data.jobs_attributes[existingJobIndex]._new,
            ...data
          };
          state.data.jobs_attributes[existingJobIndex] = newJobItem;
        }
      } else {
        newJobItem = {
          id: nanoid(),
          project_type: projectType,
          _new: true,
          ...data
        };
        state.data.jobs_attributes.push(newJobItem);
      }
    },

    /**
     * Удаляем опыт работы
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Id работы, которая будет удалена.
     */
    deleteJob: (state, { payload }: { payload: string | undefined }) => {
      if (!payload) return state;
      /** Проходим по коллекции и новый массив помещаем в jobs_attributes */
      state.data.jobs_attributes = state.data.jobs_attributes
        .map(job => {
          /** Если объект новый, его не добавляем в новый массив */
          if (job.id === payload && job._new === true) {
            return null;
          }
          /** Если объект не новый, добавляем поле _destroy: true, если id совпадает */
          if (job.id === payload) {
            return { ...job, _destroy: true };
          }
          return job;
        })
        .filter(job => job !== null);
    },

    /**
     * Переключатель редактирования контакта
     * @param state Состояние
     * @param root0 Экшен
     * @param root0.payload Id контакта
     */
    toggleEditContact: (
      state,
      { payload }: { payload: string | undefined }
    ) => {
      if (!payload || !state.data.contacts) return state;

      state.data.contacts = state.data.contacts.map(contact => ({
        ...contact,
        _edit: contact.id === payload ? !contact._edit : false
      }));
    },

    /**
     * Закрываем все открытые карточки редактирования контактов.
     * @param state - Текущее состояние редьюсера.
     */
    closeAllEditContacts: state => {
      if (!state.data.contacts) return state;

      state.data.contacts = state.data.contacts.map(contact =>
        contact._edit ? { ...contact, _edit: false } : contact
      );
    },

    /**
     * Обновляем массив контактов актёра
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Значение контакта.
     * @param action.payload.data - Данные контакта.
     * @param action.payload.id - Id контакта (если редактирование существующего).
     */
    updateContact: (
      state,
      { payload }: { payload: { data: Omit<IUserContact, 'id'>; id?: string } }
    ) => {
      const { data, id } = payload;
      if (!state.data.contacts) {
        state.data.contacts = [];
      }

      if (id) {
        const existingIndex = state.data.contacts.findIndex(
          contact => contact.id === id
        );
        if (existingIndex >= 0) {
          state.data.contacts[existingIndex] = {
            ...state.data.contacts[existingIndex],
            ...data,
            id
          };
        }
      } else {
        state.data.contacts.push({
          ...data,
          id: nanoid(),
          _new: true
        });
      }
    },

    /**
     * Удаляем контакт
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с полезной нагрузкой для обновления состояния.
     * @param action.payload - Id контакта для удаления.
     */
    deleteContact: (state, { payload }: { payload: string | undefined }) => {
      if (!payload || !state.data.contacts) return state;

      state.data.contacts = state.data.contacts
        .map(contact => {
          if (contact.id === payload && contact._new === true) {
            return null;
          }
          if (contact.id === payload) {
            return { ...contact, _destroy: true };
          }
          return contact;
        })
        .filter((contact): contact is IUserContact => contact !== null);
    },

    /**
     * Обновление значений одной категории навыков (sport / dance_style /
     * musical_instrument / vocal_style)
     * @param state - Текущее состояние редьюсера.
     * @param action - Действие с payload {category, value}.
     * @param action.payload
     * @param action.payload.category
     * @param action.payload.value
     */
    updateSkillCategory: (
      state,
      {
        payload
      }: { payload: { category: TSkillCategory; value: ISelectValue[] } }
    ) => {
      if (!state.data.skills) return state;
      state.data.skills[payload.category] = payload.value;
    },

    /**
     * Открывает/закрывает форму правки награды
     * @param state - состояние
     * @param root0 - экшен
     * @param root0.payload - id награды
     */
    toggleEditAward: (state, { payload }: { payload: IActorAward['id'] }) => {
      const award = state.data.actor_awards?.find(item => item.id === payload);

      if (award) award._edit = !award._edit;
    },

    /**
     * Добавляет награду (без `id`) или сохраняет правку (с `id`) и закрывает её форму.
     * Новая запись получает временный id и пометку `_new` — в payload она уйдёт без id
     * @param state - состояние
     * @param root0 - экшен
     * @param root0.payload - год и название, `id` правленой записи
     */
    updateAward: (state, { payload }: { payload: IAwardUpdate }) => {
      const awards = (state.data.actor_awards ??= []);
      const award = awards.find(item => item.id === payload.id);

      if (payload.id === undefined || !award) {
        awards.push({ ...payload.data, id: nanoid(), _new: true });
        return;
      }
      Object.assign(award, payload.data);
      delete award._edit;
    },

    /**
     * Удаляет награду: новую — из списка, сохранённую — помечает `_destroy`
     * @param state - состояние
     * @param root0 - экшен
     * @param root0.payload - id награды
     */
    deleteAward: (state, { payload }: { payload: IActorAward['id'] }) => {
      const awards = state.data.actor_awards ?? [];
      const target = awards.find(item => item.id === payload);

      if (!target) return;
      if (target._new) {
        state.data.actor_awards = awards.filter(item => item !== target);
      } else {
        target._destroy = true;
      }
    },

    /** Удаление данных пользователя */
    reset: () => initialState
  }
});

export const {
  update: updateProfileToEdit,
  updateData: updateProfileToEditData,
  updateErrors: updateProfileToEditErrors,
  resetErrors: resetProfileToEditErrors,
  updateProfileVisibility: updateProfileToEditProfileVisibility,
  reset: resetProfileToEdit,
  toggleEditEducation,
  updateEducation,
  deleteEducation,
  toggleEditJob,
  updateJobs,
  deleteJob,
  toggleEditContact,
  closeAllEditContacts,
  updateContact,
  deleteContact,
  updateSkillCategory,
  toggleEditAward,
  updateAward,
  deleteAward
} = profileToEditSlice.actions;
export const { reducer: profileToEditReducer } = profileToEditSlice;
